import classNames from "classnames";
import React from "react";

import {
    RatingGraphic,
    ratingGraphicRendersFor,
} from "../../../components/RatingGraphic";
import { useAppSelector } from "../../../store/reducers";
import { usePageSetting } from "../../../utils/settings";
import { defaults } from "../defaults";
import { IFilterOption } from "../models";

import styles from "../GridFilter/index.module.scss";

interface IProps {
    filterID: string;
    option: IFilterOption;
    onChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
        isSelected: boolean,
    ) => void;
}

export const GridFilterOption = React.memo((props: IProps): React.ReactNode => {
    const isSelected = useAppSelector((rootState) => {
        const state = rootState.productgrid2 || defaults;
        const filterState = state.filters[props.filterID];
        const selectedValues = filterState ? filterState.selectedValues : null;
        return selectedValues
            ? selectedValues.includes(props.option.id)
            : false;
    });

    const appSlug = usePageSetting("app_slug") || "";

    const optionNamespace = useAppSelector((rootState) => {
        const state = rootState.productgrid2 || defaults;
        return state.products[0]?.product_class_slug || "";
    });

    // Asking `RatingGraphic` whether it will draw anything rather than
    // re-deriving the rule: on a site where it renders nothing, hiding the
    // label behind it would leave checkboxes with nothing to tell them apart.
    const ratingGraphic =
        props.option.graphic?.kind === "rating" &&
        ratingGraphicRendersFor(appSlug)
            ? props.option.graphic
            : null;

    const optionClasses = classNames({
        [styles.filterOption]: true,
        [`product-grid-filter__option--${props.option.id}`]: true,
    });
    // Namespaced by filter: the option ids these carry are generic enough to
    // collide ("4" from a rating filter beside a numeric attribute value, or
    // two grids on one page each with a discount filter emitting "true").
    // Duplicate ids make every label after the first toggle the wrong box.
    // The select-one path already namespaces the same way.
    const inputID = `check-box-${props.filterID}-${props.option.id}`;

    return (
        <div className={optionClasses}>
            <input
                id={inputID}
                type="checkbox"
                className="product-grid-filter__input al-product-grid__filter-input"
                value={props.option.id}
                checked={isSelected}
                onChange={(e) => {
                    props.onChange(
                        optionNamespace,
                        props.filterID,
                        props.option.id,
                        e.currentTarget.checked,
                    );
                }}
            />
            <label htmlFor={inputID}>
                <span className={styles.filterOptionLabel}>
                    {ratingGraphic ? (
                        <>
                            {/* Decorative: whatever accessible text the
                                graphic carries describes itself, not what
                                selecting it does -- `RatingGraphic` reads
                                "Rated 4 out of 5 stars" where this option
                                means "4 stars and up". The option's own label
                                is the accurate one. */}
                            <span aria-hidden="true">
                                {/* `star-rating` rather than a filter-
                                    specific card class: `RatingGraphic` only
                                    applies its own sizing and star fill when
                                    the card class is that, so anything else
                                    renders unfilled stars at their intrinsic
                                    50px. */}
                                <RatingGraphic
                                    cardClass="star-rating"
                                    cardSize="small"
                                    rating={ratingGraphic.rating}
                                    onlyStars={true}
                                />
                            </span>
                            <span className="ada-screenreader-only">
                                {props.option.label}
                            </span>
                        </>
                    ) : (
                        props.option.label
                    )}
                </span>
            </label>
        </div>
    );
});
